'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- CMS-1925 follows the Figma promo code brand color. */

import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import {
  ATTR_NAMES,
  getAttrFromProduct,
} from '@hyphengroup/cms-components/types/attrNames';
import { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useEffect, useRef, useState } from 'react';
import { hasPersonalLoanLeadForm } from './PriorityAttribute';
import { ApplyStaticText } from '$/(cms)/-cms/apply-v3/[[...slugs]]/i18n';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

// CMS-1925: "Copied" confirmation duration for the promo code row.
const copiedFeedbackMs = 2000;

// Exposed so ProductCard can adjust the card spacing when the row is absent.
export function getProductPromoCode(product: CmsProduct): string | undefined {
  return (
    getAttrFromProduct(ATTR_NAMES.PROMO_CODE, product)?.value?.trim() ||
    undefined
  );
}

// CMS-1925: promo code row inside the apply-v3 product card. Reuses the
// PROMO_CODE product attribute — no dedicated Strapi field. Empty/null code
// renders nothing (the row and its divider are hidden together).
export function PromoCodeRow({
  product,
  isModal,
}: {
  product: CmsProduct;
  // True inside the apply-v3 modal (M1); false/undefined on the standalone
  // page (M2/M3).
  isModal?: boolean;
}) {
  const context = useClientContext();
  const staticText = getClientTranslations(ApplyStaticText, context);

  const promoCode = getProductPromoCode(product);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(copiedTimeoutRef.current), []);

  const handleCopyPromoCode = async () => {
    // Guard `navigator.clipboard` — insecure context / older browsers leave
    // it undefined (same guard as TiProviderHero).
    if (
      !promoCode ||
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.writeText !== 'function'
    ) {
      return;
    }
    try {
      await navigator.clipboard.writeText(promoCode);
      // Restart (not stack) the confirmation on repeated taps.
      window.clearTimeout(copiedTimeoutRef.current);
      setCopied(true);
      copiedTimeoutRef.current = window.setTimeout(
        () => setCopied(false),
        copiedFeedbackMs,
      );
    } catch (error) {
      console.error('Failed to copy promo code:', error);
    }
  };

  if (!promoCode) return null;

  const promoCodeWrapperClasses = classNames(
    'border-t border-dashed border-borders/default',
    {
      'pt-2 md:pt-[10.5px] lg:pt-[12.5px] xl:pt-[13.5px]': !isModal,
      'pt-2 md:pt-[10.5px]': isModal,
    },
  );

  return (
    <div
      className={promoCodeWrapperClasses}
      data-component="ApplyV3ProductCardPromoCode"
    >
      <div className="mhg-alpha-typography-body-2 flex justify-between gap-6">
        <div className="flex items-center gap-spacing/gap/soft-4px text-text/secondary-lighter">
          {staticText.promoCodeLabel}
          {hasPersonalLoanLeadForm(context.brand) && (
            <Tooltip
              content={staticText.promoCodeTooltip}
              tooltipClass="z-[var(--z-index-modal-tooltip)] max-w-[300px]"
            />
          )}
        </div>
        {/* -my-3 offsets the 44px hit area (AC minimum) so the visual row
            height stays one text line, as in the design. */}
        <button
          type="button"
          aria-label={
            copied ? staticText.selectedPromoCode : staticText.copyPromoCode
          }
          className="relative -my-3 flex min-h-11 items-center gap-1"
          onClick={handleCopyPromoCode}
        >
          <span className="whitespace-nowrap text-[#F26722]">{promoCode}</span>
          <MaterialIcon icon={copied ? 'check' : 'content_copy'} px={16} />
          {/* Absolutely positioned so the confirmation never reflows the row
              (AC: one line at 360px) while still showing "Copied" for 2s.
              Above the button, not below: the card article is overflow-hidden
              and in the modal (no accordion) the row is the card's bottom edge,
              so a below-the-button tooltip gets clipped there. */}
          {copied && (
            <span className="mhg-alpha-typography-caption absolute bottom-full right-0 rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px shadow bg-fill/card text-text/secondary-lighter">
              {staticText.copied}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
